import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { signed_by: "", designation: "", signature_text: "", signature_image: "", active: true };

export default function LabSignatures() {
  const { can } = useAuth();
  const manage = can("lab.report.sign");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try { const { data } = await api.get("/lab/signatures"); setItems(data.items); }
    catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setFormError(""); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s }); setFormError(""); setOpen(true); };
  const onFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 600000) { setFormError("Image too large (max 600 KB)."); return; }
    const reader = new FileReader(); reader.onload = () => setForm((f) => ({ ...f, signature_image: reader.result })); reader.readAsDataURL(file);
  };
  const save = async (e) => {
    e.preventDefault();
    if (!form.signed_by.trim()) { setFormError("Signed-by name is required."); return; }
    setBusy(true); setFormError("");
    try {
      if (editing) await api.patch(`/lab/signatures/${editing.signature_id}`, form);
      else await api.post("/lab/signatures", form);
      toast.success(editing ? "Signature updated." : "Signature added."); setOpen(false); load();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };
  const toggle = async (s) => { try { await api.patch(`/lab/signatures/${s.signature_id}`, { active: !s.active }); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!can("lab.orders.view")) return <NoAccess what="report signatures" />;

  return (
    <div data-testid="lab-signatures-page">
      <PageHeader overline="Diagnostics" title="Signatures"
        subtitle="Authorised report signatories. Only users with sign permission can create or use signatures."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-signature-btn"><Plus className="h-4 w-4" /> New signature</button>} />
      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="lab-signatures-table" columns={["Signed by", "Designation", "Signature", "Status", manage ? "Actions" : ""]}
          rows={items} empty={<EmptyState title="No signatures" subtitle="Add an authorised report signatory." testid="lab-signatures-empty" />}
          renderRow={(s) => (
            <tr key={s.signature_id} data-testid={`lab-signature-row-${s.signature_id}`}>
              <Td className="font-medium text-slate-900">{s.signed_by}</Td>
              <Td className="text-slate-600">{s.designation || "—"}</Td>
              <Td>{s.signature_image ? <img src={s.signature_image} alt="sig" className="h-8" /> : <span className="text-slate-500 text-sm">{s.signature_text || "—"}</span>}</Td>
              <Td><span className={`text-xs font-medium ${s.active ? "text-emerald-600" : "text-slate-400"}`}>{s.active ? "Active" : "Inactive"}</span></Td>
              {manage && <Td><div className="flex gap-2"><button onClick={() => openEdit(s)} className={ghostBtnCls} data-testid={`edit-signature-${s.signature_id}`}>Edit</button><button onClick={() => toggle(s)} className={ghostBtnCls} data-testid={`toggle-signature-${s.signature_id}`}>{s.active ? "Deactivate" : "Activate"}</button></div></Td>}
            </tr>
          )} />
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit signature" : "New signature"} testid="lab-signature-modal">
        <form onSubmit={save} className="space-y-4">
          <Field label="Signed by *"><input className={inputCls} data-testid="signature-name" value={form.signed_by} onChange={(e) => setForm({ ...form, signed_by: e.target.value })} /></Field>
          <Field label="Designation"><input className={inputCls} data-testid="signature-designation" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></Field>
          <Field label="Signature text"><input className={inputCls} data-testid="signature-text" value={form.signature_text} onChange={(e) => setForm({ ...form, signature_text: e.target.value })} /></Field>
          <Field label="Signature image (optional, ≤600 KB)"><input type="file" accept="image/*" onChange={onFile} data-testid="signature-image" /></Field>
          {form.signature_image && <img src={form.signature_image} alt="preview" className="h-12" />}
          {formError && <p className="text-sm text-red-600" data-testid="signature-form-error">{formError}</p>}
          <div className="flex justify-end gap-3"><button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button><button disabled={busy} className={primaryBtnCls} data-testid="save-signature-btn">{busy ? "Saving…" : "Save"}</button></div>
        </form>
      </Modal>
    </div>
  );
}
